import { domainError, type DomainError } from '../../shared/domain-error';
import { normalizeName } from '../../shared/naming';
import { err, ok, type Result } from '../../shared/result';
import { GANTTPRO_ERROR, type RowIssue } from './errors';
import type { ScheduleItemRowData } from './item-row';
import {
  cleanText,
  columnOf,
  findHeaderRow,
  readDate,
  readFlag,
  readNumber,
  readWbsList,
  type Cell,
  type Matrix,
} from './matrix-cells';

/**
 * Leitura do **modelo** de cronograma por item — a aba "Itens" da planilha do sistema
 * (Fase 25, ADR-161).
 *
 * É o segundo leitor do mesmo contrato: o GanttPRO não traz quantidade, e o modelo traz. É
 * por ele que entram as quantidades do levantamento, e é nele que o usuário corrige à mão o
 * que o de-para errou antes de importar. Trocar de ferramenta de cronograma é escrever
 * outro leitor — a importação continua recebendo `ScheduleItemRowData`.
 *
 * O cabeçalho é achado pelo nome, e não pela posição: a planilha ganha colunas
 * (conferência, composição, observação) que o leitor ignora, e o usuário as reordena.
 *
 * - **"(frente inteira)" na coluna Serviço é serviço nulo** — a frente inteira (RN-320);
 * - **o pavimento do modelo é nome e nível**: "14º PAVIMENTO BARRILETE", 14. O nome é o que
 *   o usuário escreveu; a comparação com o cadastro ignora a caixa (RN-036).
 */

const HEADERS = {
  wbs: ['edt'],
  tower: ['torre'],
  floor: ['pavimento'],
  level: ['nivel'],
  discipline: ['disciplina'],
  service: ['servico'],
  segment: ['trecho'],
  start: ['inicio', 'data de inicio'],
  end: ['fim', 'termino', 'data final', 'data de termino'],
  predecessors: ['predecessoras (edt)', 'predecessoras', 'predecessora'],
  quantity: ['quantidade'],
  unit: ['un', 'unidade'],
  provisional: ['quantidade provisoria', 'provisoria'],
  labor: ['hh do item (ganttpro)', 'hh do item', 'hh', 'horas-homem', 'horas homem'],
  source: ['tarefa no ganttpro', 'tarefa de origem', 'tarefa'],
} as const;

/** As colunas sem as quais não há item: onde, quando e em que frente. */
const REQUIRED: readonly (readonly string[])[] = [
  HEADERS.tower,
  HEADERS.floor,
  HEADERS.discipline,
  HEADERS.start,
  HEADERS.end,
];

const REQUIRED_LABEL = ['Torre', 'Pavimento', 'Disciplina', 'Início', 'Fim'] as const;

/** Como a coluna Serviço escreve a frente inteira. */
const WHOLE_FRONT = new Set(['(frente inteira)', 'frente inteira']);

const SCAN_LIMIT = 30;

export interface ScheduleItemTemplateReading {
  readonly rows: ScheduleItemRowData[];
  /** Linhas que não puderam ser lidas — as outras seguem. */
  readonly issues: RowIssue[];
  readonly headerRowNumber: number;
}

/** A matriz tem o cabeçalho do modelo? Serve para a tela decidir qual leitor usar. */
export function looksLikeScheduleItemTemplate(matrix: Matrix): boolean {
  return findHeaderRow(matrix, REQUIRED, SCAN_LIMIT) !== undefined;
}

export function readScheduleItemTemplate(
  matrix: Matrix,
): Result<ScheduleItemTemplateReading, DomainError> {
  const header = findHeaderRow(matrix, REQUIRED, SCAN_LIMIT);
  if (header === undefined) {
    return err(
      domainError(
        GANTTPRO_ERROR.TEMPLATE_HEADER_NOT_FOUND,
        'Não encontrei o cabeçalho do modelo de cronograma por item — as colunas ' +
          `${REQUIRED_LABEL.map((label) => `"${label}"`).join(', ')} — nas primeiras ` +
          `${String(SCAN_LIMIT)} linhas desta aba. É a aba "Itens" da planilha do sistema?`,
      ),
    );
  }

  const column = (spellings: readonly string[]) => columnOf(header.columns, spellings);
  const towerColumn = column(HEADERS.tower);
  const floorColumn = column(HEADERS.floor);
  const disciplineColumn = column(HEADERS.discipline);
  const startColumn = column(HEADERS.start);
  const endColumn = column(HEADERS.end);
  if (
    towerColumn === undefined ||
    floorColumn === undefined ||
    disciplineColumn === undefined ||
    startColumn === undefined ||
    endColumn === undefined
  ) {
    // `findHeaderRow` já garantiu as cinco; o teste é para o compilador, e a mensagem para o caso.
    return err(
      domainError(
        GANTTPRO_ERROR.TEMPLATE_MISSING_COLUMNS,
        `Faltam colunas no modelo: ${REQUIRED_LABEL.join(', ')}.`,
      ),
    );
  }

  const wbsColumn = column(HEADERS.wbs);
  const levelColumn = column(HEADERS.level);
  const serviceColumn = column(HEADERS.service);
  const segmentColumn = column(HEADERS.segment);
  const predecessorsColumn = column(HEADERS.predecessors);
  const quantityColumn = column(HEADERS.quantity);
  const unitColumn = column(HEADERS.unit);
  const provisionalColumn = column(HEADERS.provisional);
  const laborColumn = column(HEADERS.labor);
  const sourceColumn = column(HEADERS.source);

  const rows: ScheduleItemRowData[] = [];
  const issues: RowIssue[] = [];

  for (let index = header.rowIndex + 1; index < matrix.length; index += 1) {
    const cells = matrix[index] ?? [];
    const rowNumber = index + 1;
    const at = (position: number | undefined): unknown =>
      position === undefined ? undefined : cells[position];

    const towerName = cleanText(cells[towerColumn]);
    const floorName = cleanText(cells[floorColumn]);
    const disciplineName = cleanText(cells[disciplineColumn]);
    // Linha em branco (ou de rodapé): nada nas três colunas que identificam a frente.
    if (towerName === null && floorName === null && disciplineName === null) continue;

    if (towerName === null || floorName === null || disciplineName === null) {
      issues.push({
        rowNumber,
        message: 'A linha precisa de Torre, Pavimento e Disciplina, e uma delas está vazia.',
      });
      continue;
    }

    const start = readDate(cells[startColumn]);
    const end = readDate(cells[endColumn]);
    if (!start.ok || start.value === null || !end.ok || end.value === null) {
      issues.push({
        rowNumber,
        message: `${floorName} / ${disciplineName}: Início ou Fim vazio ou ilegível.`,
      });
      continue;
    }

    const level = readNumber(at(levelColumn));
    const quantity = readNumber(at(quantityColumn));
    const labor = readNumber(at(laborColumn));
    const bad = firstBad([
      ['Nível', level, (value) => !Number.isInteger(value)],
      ['Quantidade', quantity],
      ['HH do item', labor],
    ]);
    if (bad !== undefined) {
      issues.push({
        rowNumber,
        message: `${floorName} / ${disciplineName}: ${bad} não é um número válido.`,
      });
      continue;
    }

    const serviceText = cleanText(at(serviceColumn));
    rows.push({
      rowNumber,
      towerName,
      floorName,
      floorLevel: level.ok ? level.value : null,
      disciplineName,
      serviceName:
        serviceText === null || WHOLE_FRONT.has(normalizeName(serviceText)) ? null : serviceText,
      segment: cleanText(at(segmentColumn)),
      wbsCode: cleanText(at(wbsColumn)),
      sourceName: cleanText(at(sourceColumn)),
      plannedStart: start.value,
      plannedEnd: end.value,
      predecessorWbsCodes:
        predecessorsColumn === undefined ? [] : readWbsList(at(predecessorsColumn)),
      quantity: quantity.ok ? quantity.value : null,
      unit: cleanText(at(unitColumn)),
      quantityProvisional: readFlag(at(provisionalColumn)),
      laborHours: labor.ok ? labor.value : null,
    });
  }

  if (rows.length === 0) {
    return err(
      domainError(
        GANTTPRO_ERROR.TEMPLATE_NO_ROWS,
        'O modelo tem o cabeçalho, mas nenhuma linha de item abaixo dele.',
        { issues: issues.length },
      ),
    );
  }

  return ok({ rows, issues, headerRowNumber: header.rowIndex + 1 });
}

/** O nome da primeira coluna cujo valor não se lê (ou que a regra extra recusa). */
function firstBad(
  checks: readonly (readonly [string, Cell<number>, ((value: number) => boolean)?])[],
): string | undefined {
  for (const [label, cell, refuse] of checks) {
    if (!cell.ok) return label;
    if (cell.value !== null && refuse?.(cell.value) === true) return label;
  }
  return undefined;
}
